import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "services", label: "Services" },
  { id: "portfolio", label: "Portfolio" },
  { id: "pricing", label: "Pricing" },
  { id: "process", label: "Process" },
  { id: "faq", label: "FAQ" },
];

export default function Navbar({ onOpenLead, onOpenBookCall, hasBanner = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        hasBanner ? "top-10" : "top-0"
      } ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between transition-all ${scrolled ? "h-16 md:h-20" : "h-20 md:h-24"}`}>
        <a
          href="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <img
            src="/bellso-logo.png"
            alt="bellso — premium web design studio"
            className="h-16 md:h-20 w-auto rounded-full"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={NAV.link(l.id)}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-medium text-zinc-300 hover:text-[#CCFF00] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="nav-cta-book-call"
            onClick={onOpenBookCall}
            className="text-sm font-medium text-zinc-300 hover:text-[#CCFF00] transition-colors"
          >
            Book a call
          </button>
          <Button
            data-testid={NAV.ctaQuote}
            onClick={() => {
              const el = document.getElementById("pricing");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-neon rounded-none px-6 py-2 h-11"
          >
            Start Your Website
          </Button>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-4">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`${NAV.link(l.id)}-mobile`}
              onClick={() => scrollTo(l.id)}
              className="block w-full text-left text-zinc-200 hover:text-[#CCFF00] py-2"
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="nav-cta-quote-mobile"
            onClick={() => {
              setOpen(false);
              const el = document.getElementById("pricing");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-neon rounded-none w-full"
          >
            Start Your Website
          </Button>
          <button
            data-testid="nav-cta-book-call-mobile"
            onClick={() => {
              setOpen(false);
              onOpenBookCall();
            }}
            className="block w-full text-center text-[#CCFF00] hover:text-white py-2 mt-2 font-medium"
          >
            Or book a strategy call
          </button>
        </div>
      )}
    </motion.header>
  );
}
